import { FC, ReactNode } from 'react'
import { Badge, Box, Button, Flex, Icon, Select, Text, Tooltip, VStack } from '@chakra-ui/react'
import { FiChevronLeft } from 'react-icons/fi'
import { SidePanelNavProps } from './types'
import { analyticsAction, trackEvent } from '../../utils/analytics'

const CollapseChevronIcon: FC = () => <Icon as={FiChevronLeft} boxSize="12px" aria-hidden />

const NavTabIcon: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Flex w="16px" h="16px" align="center" justify="center" flexShrink={0} opacity={0.75}>
    {children}
  </Flex>
)

const CollapsibleNavText: FC<{ isCollapsed: boolean; children: ReactNode; maxWidth?: string }> = ({
  isCollapsed,
  children,
  maxWidth = '160px',
}) => (
  <Box
    as="span"
    display="inline-block"
    opacity={isCollapsed ? 0 : 1}
    maxW={isCollapsed ? 0 : maxWidth}
    overflow="hidden"
    whiteSpace="nowrap"
    verticalAlign="middle"
    transition="opacity 0.15s ease, max-width 0.22s ease"
    aria-hidden={isCollapsed}
  >
    {children}
  </Box>
)

const CollapsedGroupDivider: FC<{ isFirstGroup: boolean }> = ({ isFirstGroup }) => (
  <Box px={2} pt={isFirstGroup ? 0 : 1.5} pb={1.5}>
    <Box h="1px" bg="gray.200" opacity={0.5} />
  </Box>
)

const selectStyles = {
  h: '42px',
  mb: '16px',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: 'medium',
  bg: 'white',
  borderColor: 'gray.200',
  _focus: {
    borderColor: 'blue.500',
    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.15)',
  },
}

export const SidePanelNav: FC<SidePanelNavProps> = ({
  activeId,
  groups,
  isCollapsed,
  onToggleCollapse,
  onChange,
  ariaLabel,
  isMobileView,
}) => {
  if (isMobileView) {
    const hasMultipleGroups = groups.filter((group) => group.label).length > 1

    return (
      <Select
        value={activeId}
        onChange={(event) => {
          const action = groups
            .flatMap((group) => group.items)
            .find((item) => item.id === event.target.value)?.action
          if (action) trackEvent('tab_selected', { action })
          onChange(event.target.value)
        }}
        aria-label={ariaLabel}
        {...selectStyles}
      >
        {hasMultipleGroups
          ? groups.map((group) =>
              group.label ? (
                <optgroup key={group.label} label={group.label}>
                  {group.items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </optgroup>
              ) : (
                group.items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))
              )
            )
          : groups.flatMap((group) =>
              group.items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))
            )}
      </Select>
    )
  }

  return (
    <Box position="sticky" top="16px">
      <Box
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="12px"
        p={isCollapsed ? '8px 6px' : '10px 8px'}
        transition="padding 0.2s ease"
        role="navigation"
        aria-label={ariaLabel}
        overflow="hidden"
      >
        <Tooltip
          label={isCollapsed ? 'Expand' : 'Collapse'}
          placement="right"
          hasArrow
          isDisabled={!isCollapsed}
        >
          <Button
            w="100%"
            display="flex"
            alignItems="center"
            justifyContent={isCollapsed ? 'center' : 'flex-start'}
            gap={isCollapsed ? 0 : 2.5}
            variant="ghost"
            size="sm"
            h="auto"
            minH="36px"
            mb={isCollapsed ? 1.5 : 2}
            px={isCollapsed ? 0 : 3}
            py={2}
            borderRadius="8px"
            color="gray.500"
            fontSize="13px"
            fontWeight="medium"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            _hover={{
              bg: 'gray.100',
              color: 'gray.700',
              '& > div:first-of-type': { bg: 'blue.50', color: 'blue.600' },
            }}
            transition="all 0.12s ease"
          >
            <Flex
              w="22px"
              h="22px"
              borderRadius="6px"
              bg="gray.100"
              align="center"
              justify="center"
              color="gray.600"
              flexShrink={0}
              transition="background 0.15s ease, color 0.15s ease"
            >
              <Flex
                transform={isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)'}
                transition="transform 0.22s ease"
              >
                <CollapseChevronIcon />
              </Flex>
            </Flex>
            <CollapsibleNavText isCollapsed={isCollapsed} maxWidth="120px">
              Collapse
            </CollapsibleNavText>
          </Button>
        </Tooltip>

        <VStack align="stretch" spacing={isCollapsed ? 0 : 1.5} transition="gap 0.22s ease">
          {groups.map((group, groupIndex) => (
            <Box key={group.label ?? groupIndex}>
              {group.label ? (
                isCollapsed ? (
                  <CollapsedGroupDivider isFirstGroup={groupIndex === 0} />
                ) : (
                  <Text
                    fontSize="10px"
                    fontWeight="bold"
                    color="gray.400"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    px={3}
                    pt={groupIndex === 0 ? 0 : 1}
                    pb={1}
                  >
                    {group.label}
                  </Text>
                )
              ) : null}

              <VStack align="stretch" spacing={0.5}>
                {group.items.map((item) => {
                  const isActive = activeId === item.id

                  return (
                    <Tooltip
                      key={item.id}
                      label={item.label}
                      placement="right"
                      isDisabled={!isCollapsed}
                      hasArrow
                    >
                      <Button
                        aria-current={isActive ? 'page' : undefined}
                        display="flex"
                        alignItems="center"
                        justifyContent={isCollapsed ? 'center' : 'flex-start'}
                        gap={isCollapsed ? 0 : 2.5}
                        variant="ghost"
                        size="sm"
                        h="auto"
                        py={2}
                        px={isCollapsed ? 2 : 3}
                        fontSize="13px"
                        fontWeight={isActive ? 'semibold' : 'medium'}
                        color={isActive ? 'blue.700' : 'gray.600'}
                        bg={isActive ? 'blue.50' : 'transparent'}
                        position="relative"
                        overflow="hidden"
                        _hover={{ bg: isActive ? 'blue.50' : 'gray.100', color: 'gray.800' }}
                        onClick={() => onChange(item.id)}
                        transition="all 0.12s ease"
                        {...(item.action && analyticsAction(item.action))}
                      >
                        <NavTabIcon>{item.icon}</NavTabIcon>
                        <CollapsibleNavText isCollapsed={isCollapsed}>
                          {item.label}
                        </CollapsibleNavText>
                        {item.badge ? (
                          <Badge
                            ml={isCollapsed ? 0 : 'auto'}
                            fontSize="10px"
                            fontWeight="semibold"
                            bg="yellow.100"
                            color="yellow.800"
                            borderRadius="full"
                            px={1.5}
                            opacity={isCollapsed ? 0 : 1}
                            maxW={isCollapsed ? 0 : '80px'}
                            overflow="hidden"
                            whiteSpace="nowrap"
                            transition="opacity 0.15s ease, max-width 0.22s ease, margin 0.22s ease"
                            aria-hidden={isCollapsed}
                          >
                            {item.badge}
                          </Badge>
                        ) : null}
                        {isActive ? (
                          <Box
                            position="absolute"
                            left={0}
                            top="6px"
                            bottom="6px"
                            w="3px"
                            borderRadius="0 3px 3px 0"
                            bg="blue.600"
                          />
                        ) : null}
                      </Button>
                    </Tooltip>
                  )
                })}
              </VStack>
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  )
}
